// THE design tokens file (FR-020, FR-021, FR-023; constitution VII, X; data-model §8; research R7).
// Values are copied verbatim from ../orchestration/design-tokens.md (extracted from the design canvas
// on 2026-10-03). Each colour keeps its canvas key in a comment. Values the canvas does not define are
// derived from the nearest canvas role and marked `// derived`. Never invent values here.

const light = {
  background: "#FBF8F2", // bg
  surface: "#FFFFFF", // sf
  surfaceAlt: "#F3EEE4", // sf2
  border: "#E6DECF", // bd
  text: "#14204A", // tx
  textMuted: "#57608A", // mu
  primary: "#C8283A", // pr
  primaryText: "#C8283A", // prt
  primarySoft: "#FBE6E9", // prbg
  accent: "#14204A", // ac
  onAccent: "#FFFFFF", // act
  success: "#17703F", // ok
  successSoft: "#DFF3E7", // okbg
  warning: "#8A5200", // wn
  warningSoft: "#FCEDCB", // wnbg
  info: "#0B6580", // in
  infoSoft: "#DAF0F7", // inbg
  focusRing: "#C8283A", // derived: pr
  crestBackground: "#14204A", // derived: ac
  crestText: "#FFFFFF", // derived: act
  envBadge: "#FCEDCB", // derived: wnbg
  envBadgeText: "#8A5200", // derived: wn
  offline: "#DAF0F7", // derived: inbg
  offlineText: "#0B6580", // derived: in
  badge: "#C8283A", // derived: pr
  badgeText: "#FFFFFF", // derived: button text on pr
  scrim: "rgba(12, 17, 34, 0.5)", // 006: dialog backdrop
};

type Palette = { readonly [K in keyof typeof light]: string };

const dark: Palette = {
  background: "#0C1122", // bg
  surface: "#151C35", // sf
  surfaceAlt: "#1D2644", // sf2
  border: "#2A3559", // bd
  text: "#EDF0FA", // tx
  textMuted: "#A3ACCC", // mu
  primary: "#D63A4B", // pr
  primaryText: "#FF8593", // prt
  primarySoft: "#3A1C29", // prbg
  accent: "#EDF0FA", // ac
  onAccent: "#0C1122", // act
  success: "#63D690", // ok
  successSoft: "#153A2A", // okbg
  warning: "#F5BC52", // wn
  warningSoft: "#3A2E12", // wnbg
  info: "#66CDEB", // in
  infoSoft: "#12313F", // inbg
  focusRing: "#D63A4B", // derived: pr
  crestBackground: "#EDF0FA", // derived: ac
  crestText: "#0C1122", // derived: act
  envBadge: "#3A2E12", // derived: wnbg
  envBadgeText: "#F5BC52", // derived: wn
  offline: "#12313F", // derived: inbg
  offlineText: "#66CDEB", // derived: in
  badge: "#D63A4B", // derived: pr
  badgeText: "#FFFFFF", // derived: button text on pr
  scrim: "rgba(12, 17, 34, 0.5)", // 006: dialog backdrop
};

export type ColorRole = keyof typeof light;
export type ThemeName = "light" | "dark";

export const tokens = {
  color: {
    light: light as Palette,
    dark,
    /** Button text on `pr` is always white (both themes). */
    onPrimary: "#FFFFFF",
    /** Akaiva mark and category accents, same in both themes (PRD §7). */
    brand: {
      a1: "#E4577A",
      a2: "#F2A900",
      a3: "#2F9E5B",
      a4: "#1AA3C6",
      a5: "#F07A28",
    },
  },
  /** Card shadow `sh` (React Native `boxShadow` string, supported on both targets). */
  shadow: {
    light: {
      card: "0 1px 2px rgba(20,32,74,.06), 0 8px 24px rgba(20,32,74,.06)",
    },
    dark: { card: "0 1px 2px rgba(0,0,0,.3), 0 8px 24px rgba(0,0,0,.25)" },
  },
  font: {
    /** Poppins, headings. Family names equal the bundled TTF names. */
    heading: {
      medium: "Poppins_500Medium",
      semiBold: "Poppins_600SemiBold",
      bold: "Poppins_700Bold",
    },
    /** DM Sans, body text. */
    body: {
      regular: "DMSans_400Regular",
      medium: "DMSans_500Medium",
      semiBold: "DMSans_600SemiBold",
      bold: "DMSans_700Bold",
    },
    /** System sans stack used while web fonts load (FR-021). */
    fallback:
      'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  /** px. `base` 14 is the body size. */
  fontSize: {
    xxs: 11,
    xs: 12,
    sm: 13,
    base: 14,
    md: 15,
    lg: 16,
    xl: 18,
    x2l: 20,
    x3l: 24,
    x4l: 28,
    x5l: 32,
    x6l: 40,
  },
  /** Line-height multiplier; use `lineHeightFor(size)` for React Native's absolute value. */
  lineHeight: { base: 1.4 },
  /** 006: letter spacing, px (the join code is shown spaced, FR-018). */
  letterSpacing: { code: 6 },
  /** Gaps, px. */
  space: {
    xxs: 4,
    xs: 6,
    sm: 8,
    smd: 10,
    md: 12,
    lg: 16,
    xl: 20,
    x2l: 24,
    x3l: 32,
  },
  radius: {
    card: 16,
    panel: 14,
    softPanel: 12,
    button: 12,
    buttonSm: 10,
    buttonLg: 14,
    input: 12,
    pill: 99,
  },
  borderWidth: {
    card: 1,
    input: 1.5,
    focusRing: 2 /* derived: visible focus outline */,
  },
  size: {
    touchTarget: 44,
    button: 44,
    buttonSm: 36,
    buttonLg: 52,
    input: 44,
    iconButton: 44,
    chip: 26,
    tab: 38,
    headerHeightDesktop: 72,
    avatar: 40,
    badge: 20,
    icon: { sm: 16, md: 20, lg: 26, xl: 40 },
    iconStroke: 1.8,
    navUnderline: 3,
    crest: 40, // derived: avatar size
    sideNav: 248, // 006: school-admin side navigation (board SABanners)
    qrPreview: 176, // 006: on-screen QR preview
  },
  /** Web only: at or above this width the desktop chrome is used (research R7). */
  breakpoint: { desktop: 1024 },
  /** Text/background pairs used by the shell; tested at 4.5:1 or more in both themes (SC-005). */
  contrastPairs: [
    ["text", "background"],
    ["text", "surface"],
    ["textMuted", "background"],
    ["textMuted", "surface"],
    ["primaryText", "background"],
    ["primaryText", "surface"],
    ["primaryText", "primarySoft"],
    ["onAccent", "accent"],
    ["crestText", "crestBackground"],
    ["envBadgeText", "envBadge"],
    ["offlineText", "offline"],
    ["badgeText", "badge"],
    // 002 forms and messages (SC-010): field error text in prt, the inline message tones.
    ["success", "successSoft"],
    ["warning", "warningSoft"],
    ["info", "infoSoft"],
  ] as const satisfies readonly (readonly [ColorRole, ColorRole])[],
  /**
   * Non-text pairs (borders, outlines), tested at 3:1 or more (WCAG 1.4.11). 002: the field error
   * border is `pr` on `sf`; `pr` is not used for text on `sf` (dark is 3.65:1), `prt` is.
   */
  nonTextContrastPairs: [
    ["primary", "surface"],
    ["focusRing", "surface"],
  ] as const satisfies readonly (readonly [ColorRole, ColorRole])[],
} as const;

export type Tokens = typeof tokens;

/** Absolute line height for a font size (React Native needs px). */
export function lineHeightFor(
  fontSize: number,
  multiplier: number = tokens.lineHeight.base,
): number {
  return Math.round(fontSize * multiplier);
}
